import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBulkUpdateTickets, useResources, useResourceRoles } from '@/api/hooks';
import type { PicklistValue } from '@/api/client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ticket ids to edit. */
  ids: number[];
  picklists: {
    status: PicklistValue[];
    priority: PicklistValue[];
    issueType: PicklistValue[];
    subIssueType: PicklistValue[];
  };
  /** Called after a successful apply (e.g. to clear the grid selection). */
  onApplied?: () => void;
}

const selectClass =
  'h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Label + control row. The control's blank/"keep existing" value means the field is left untouched. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid grid-cols-[6.5rem_1fr] items-center gap-3">
      <span className="text-sm text-foreground">{label}</span>
      {children}
    </label>
  );
}

export function BulkEditTicketsDialog({ open, onOpenChange, ids, picklists, onApplied }: Props) {
  const resources = useResources();
  const mutation = useBulkUpdateTickets();

  // Empty string = "keep existing" (field not sent). Any other value = overwrite.
  const [statusVal, setStatusVal] = useState('');
  const [priorityVal, setPriorityVal] = useState('');
  const [assigneeVal, setAssigneeVal] = useState(''); // '' | 'unassign' | resourceId
  const [roleVal, setRoleVal] = useState('');         // role for the chosen assignee (Autotask requires it)
  const [issueTypeVal, setIssueTypeVal] = useState('');
  const [subTypeVal, setSubTypeVal] = useState('');

  // Reset all controls whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setStatusVal('');
      setPriorityVal('');
      setAssigneeVal('');
      setRoleVal('');
      setIssueTypeVal('');
      setSubTypeVal('');
    }
  }, [open]);

  const resourceItems = resources.data?.items ?? [];
  const sortedResources = useMemo(
    () => [...resourceItems].sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    [resourceItems],
  );

  // When a specific resource is chosen, load that resource's roles (same two-step flow as the
  // inline AssignedResourcePicker). Autotask rejects an assignment that has no role.
  const assigneeResourceId =
    assigneeVal && assigneeVal !== 'unassign' ? Number(assigneeVal) : null;
  const roles = useResourceRoles(assigneeResourceId);
  const roleItems = roles.data?.items ?? [];

  // Default to the resource's first role once they load (only while none is picked yet).
  useEffect(() => {
    if (assigneeResourceId != null && !roleVal && roleItems.length > 0) {
      setRoleVal(String(roleItems[0].id));
    }
  }, [assigneeResourceId, roleItems, roleVal]);

  // Assignee picked but no role available/selected yet — can't apply.
  const assigneeNeedsRole = assigneeResourceId != null && !roleVal;

  // Sub-Type options are scoped to the chosen Type (parentValue match), like the inline editor.
  const subTypeOptions = useMemo(() => {
    if (!issueTypeVal) return [] as PicklistValue[];
    return picklists.subIssueType.filter((o) => Number(o.parentValue) === Number(issueTypeVal));
  }, [picklists.subIssueType, issueTypeVal]);

  const hasChanges = Boolean(statusVal || priorityVal || assigneeVal || issueTypeVal);

  function buildFields(): Record<string, number | null> {
    const fields: Record<string, number | null> = {};
    if (statusVal) fields.status = Number(statusVal);
    if (priorityVal) fields.priority = Number(priorityVal);
    if (assigneeVal === 'unassign') {
      fields.assignedResourceID = null;
      fields.assignedResourceRoleID = null;
    } else if (assigneeVal) {
      fields.assignedResourceID = Number(assigneeVal);
      fields.assignedResourceRoleID = roleVal ? Number(roleVal) : null;
    }
    if (issueTypeVal) {
      fields.issueType = Number(issueTypeVal);
      // Changing Type rewrites Sub-Type: the chosen child, or null to clear a now-invalid one.
      fields.subIssueType = subTypeVal ? Number(subTypeVal) : null;
    }
    return fields;
  }

  const handleApply = () => {
    const fields = buildFields();
    if (Object.keys(fields).length === 0) {
      toast.error('Change at least one field first.');
      return;
    }
    mutation.mutate(
      { ids, fields },
      {
        onSuccess: (res) => {
          if (res.failed > 0) {
            toast.warning(`Updated ${res.updated} of ${ids.length}`, {
              description: `${res.failed} failed — they may need different field values.`,
            });
          } else {
            toast.success(`Updated ${res.updated} ticket${res.updated === 1 ? '' : 's'}`);
          }
          onApplied?.();
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error('Bulk update failed', {
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk edit {ids.length} ticket{ids.length === 1 ? '' : 's'}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Set any field to apply it to every selected ticket. Fields left on “Keep existing” are untouched.
          </p>
        </DialogHeader>

        <div className="space-y-3 px-6 pb-2">
          <Field label="Status">
            <select className={selectClass} value={statusVal} onChange={(e) => setStatusVal(e.target.value)}>
              <option value="">Keep existing</option>
              {picklists.status.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Priority">
            <select className={selectClass} value={priorityVal} onChange={(e) => setPriorityVal(e.target.value)}>
              <option value="">Keep existing</option>
              {picklists.priority.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Assignee">
            <select
              className={selectClass}
              value={assigneeVal}
              onChange={(e) => { setAssigneeVal(e.target.value); setRoleVal(''); }}
            >
              <option value="">Keep existing</option>
              <option value="unassign">Unassigned</option>
              {sortedResources.map((r) => (
                <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
              ))}
            </select>
          </Field>

          {/* Role for the chosen assignee — Autotask requires it. Defaults to their first role. */}
          {assigneeResourceId != null ? (
            <Field label="Role">
              {roles.isLoading ? (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading roles…
                </span>
              ) : roleItems.length === 0 ? (
                <span className="text-sm text-destructive">No roles found for this resource</span>
              ) : (
                <select className={selectClass} value={roleVal} onChange={(e) => setRoleVal(e.target.value)}>
                  {roleItems.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          <Field label="Type">
            <select
              className={selectClass}
              value={issueTypeVal}
              onChange={(e) => { setIssueTypeVal(e.target.value); setSubTypeVal(''); }}
            >
              <option value="">Keep existing</option>
              {picklists.issueType.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          {/* Sub-Type only applies when Type is being changed; it follows the chosen Type. */}
          {issueTypeVal ? (
            <Field label="Sub-Type">
              <select className={selectClass} value={subTypeVal} onChange={(e) => setSubTypeVal(e.target.value)}>
                <option value="">(clear sub-type)</option>
                {subTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply} disabled={mutation.isPending || !hasChanges || assigneeNeedsRole}>
            {mutation.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Apply to {ids.length}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
